import type { TextFieldProps } from '@mui/material/TextField';

import { Controller, useFormContext } from 'react-hook-form';
import { transformValue, transformValueOnBlur, transformValueOnChange } from 'minimal-shared/utils';

import TextField from '@mui/material/TextField';

// ----------------------------------------------------------------------

export type RHFTextFieldProps = TextFieldProps & {
  name: string;
};

export function RHFTextField({
  name,
  helperText,
  slotProps = {},
  type = 'text',
  ...other
}: RHFTextFieldProps) {
  const { control } = useFormContext();

  const isNumberType = type === 'number';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const displayValue = isNumberType ? transformValue(field.value) : field.value;
        // El label flotante de MUI a veces no calcula bien su estado "shrink" cuando el valor
        // se rellena por codigo (setValue, ej. autorrelleno de cliente) en vez de por tecleo del
        // usuario, y queda superpuesto con el valor. Forzamos shrink en cuanto hay valor; si esta
        // vacio no tocamos nada y MUI sigue decidiendolo solo (foco, etc.).
        const hasValue = displayValue !== undefined && displayValue !== null && displayValue !== '';

        return (
          <TextField
            {...field}
            fullWidth
            value={displayValue}
            onChange={(event) => {
              const transformedValue = isNumberType
                ? transformValueOnChange(event.target.value)
                : event.target.value;

              field.onChange(transformedValue);
            }}
            onBlur={(event) => {
              const transformedValue = isNumberType
                ? transformValueOnBlur(event.target.value)
                : event.target.value;

              field.onChange(transformedValue);
            }}
            type={isNumberType ? 'text' : type}
            error={!!error}
            helperText={error?.message ?? helperText}
            slotProps={{
              ...slotProps,
              htmlInput: {
                autoComplete: 'off',
                ...slotProps?.htmlInput,
                ...(isNumberType && { inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }),
              },
              inputLabel: {
                ...(hasValue && { shrink: true }),
                ...slotProps?.inputLabel,
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}
